import React, { useEffect, useMemo, useRef, useState } from "react";
import { getProjectDetailService, getProjectFilterListService } from "@/service/project.service";
import { getEmailTemplateService } from "@/service/email.service";
import { getClientDetailService } from "@/service/client.service";
import { FilterSelectWithRadio } from "@/components/form";
import QuillEditor from "./QuillEditor";

export type Option = { value: string; label: string };

export type NewEmailFormData = {
  client: string;
  client_user_id?: string;
  org_id?: string;
  project: string;
  scenario: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  content: string;
  end_date?: string;
  esti_hrs?: string | number;
  util_hrs?: string | number;
  remi_hrs?: string | number;
};

const InfoField: React.FC<{ label: string; value?: string; danger?: boolean }> = ({ label, value, danger }) => (
  <div className="flex flex-col gap-1 w-full">
    <label className="text-xs text-[#6C6C6C]">{label}</label>
    <div className={`h-[38px] px-3 flex items-center border border-gray-200 rounded-[6px] text-sm bg-[#FFFFFF] ${danger ? "text-[#DC2626]" : "text-[#1E1E1E]"}`}>
      {value || "-"}
    </div>
  </div>
);

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <label className="text-sm text-[#1E1E1E] font-[500]">
    {label}
    {required ? <span className="text-[#FF3B30]">*</span> : null}
  </label>
);

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

// formats are handled internally by Quill; keeping modules for toolbar

const NewEmailForm: React.FC<{
  clientOptions: Option[];
  projectOptions: Option[];
  scenarioOptions: Option[];
  onSubmit: (data: NewEmailFormData) => void;
  onCancel: () => void;
  prefill?: { clientId: string; projectId: string } | null;
  onViewEmails?: (date: string) => void;
}> = ({ clientOptions, projectOptions, scenarioOptions, onSubmit, onCancel, prefill, onViewEmails }) => {
  const [form, setForm] = useState<NewEmailFormData>({
    client: "",
    project: "",
    scenario: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    content: "",
  });

  const [projectOptionsLocal, setProjectOptionsLocal] = useState<Option[]>([]);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [estimatedHours, setEstimatedHours] = useState<string | undefined>(undefined);
  const [utilizedHours, setUtilizedHours] = useState<string | undefined>(undefined);
  const [remainingHours, setRemainingHours] = useState<string | undefined>(undefined);
  const [lastEmailDate, setLastEmailDate] = useState<string | undefined>(undefined);
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const didApplyPrefill = useRef(false);

  // For the form scenario dropdown, only allow three scenarios:
  // Project expired, Out of hours, Still have hours.
  const limitedScenarioOptions = useMemo(
    () =>
      scenarioOptions.filter((opt) =>
        ["Project expired", "Out of hours", "Still have hours"].includes(opt.value)
      ),
    [scenarioOptions]
  );

  const isRemainingNegative = useMemo(() => {
    if (remainingHours == null) return false;
    const num = parseFloat(String(remainingHours).toString());
    return !Number.isNaN(num) && num < 0;
  }, [remainingHours]);

  const isEndDatePast = useMemo(() => {
    if (!endDate) return false;
    let d: Date | null = null;
    // Try ISO/native first
    const native = new Date(endDate);
    if (!Number.isNaN(native.getTime())) {
      d = native;
    } else {
      // Fallback for DD/MM/YYYY format (e.g., 20/05/2025)
      const m = /^([0-3]?\d)\/([0-1]?\d)\/(\d{4})$/.exec(endDate.trim());
      if (m) {
        const day = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        const year = parseInt(m[3], 10);
        const tmp = new Date(year, month, day);
        // Validate constructed date (handles invalid like 32/13/2025)
        if (
          tmp.getFullYear() === year &&
          tmp.getMonth() === month &&
          tmp.getDate() === day
        ) {
          d = tmp;
        }
      }
    }
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }, [endDate]);

  const maybeFetchEmailTemplate = async (
    nextScenario: string,
    nextProjectId: string
  ) => {
    if (!nextScenario || !nextProjectId) return;
    try {
      setLoadingTemplate(true);
      const { data } = await getEmailTemplateService(nextScenario, nextProjectId);
      const tmpl = data?.data;
      if (tmpl) {
        setForm((s) => ({
          ...s,
          subject: tmpl.subject || s.subject,
          content: tmpl.body || s.content,
        }));
      }
    } catch (e) {
      // ignore — user can still enter subject/content manually
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleClientChange = async (v: string) => {
    const selectedOption = clientOptions.find((opt) => opt.value === v);
    setForm((s) => ({ ...s, client: v, client_user_id: selectedOption?.userId, org_id: v, project: "" }));
    // clear project-derived fields
    setEndDate(undefined);
    setEstimatedHours(undefined);
    setUtilizedHours(undefined);
    setRemainingHours(undefined);
    if (!v) {
      setProjectOptionsLocal([]);
      return;
    }
    try {
      setLoadingClient(true);
      const { data } = await getClientDetailService(v);
      const details = data?.data;
      const email = details?.user?.email || details?.client?.email || "";
      if (email) {
        setForm((s) => ({ ...s, to: email }));
      }
      // fetch projects for selected client
      const projRes = await getProjectFilterListService({ search: "", clientId: v, projectType: "block of time" });
      const opts: Option[] = (projRes.data?.data || []).map((p: any) => ({
        value: p.id,
        label: p.name,
      }));
      setProjectOptionsLocal(opts);
    } catch (e) {
      // ignore errors on client fetch
    } finally {
      setLoadingClient(false);
    }
  };

  const handleProjectChange = async (v: string) => {
    setForm((s) => ({ ...s, project: v }));
    if (!v) {
      setEndDate(undefined);
      setEstimatedHours(undefined);
      setUtilizedHours(undefined);
      setRemainingHours(undefined);
      setLastEmailDate(undefined);
      return;
    }
    try {
      setLoadingProject(true);
      const { data } = await getProjectDetailService(v);
      const proj = data?.data;

      const end = proj?.end_date ? proj.end_date : undefined;
      const esti = (proj?.tracker?.total_esti_hrs ?? proj?.esti_hrs) ?? undefined;
      const util = (proj?.tracker?.util_hrs ?? proj?.util_hrs) ?? undefined;
      const rem = (proj?.tracker?.remi_hrs ?? proj?.remi_hrs) ?? undefined;
      const nextScenario = proj?.project_scenario ? String(proj.project_scenario) : "";
      setEndDate(end);
      setEstimatedHours(esti != null ? String(esti) : undefined);
      setUtilizedHours(util != null ? String(util) : undefined);
      setRemainingHours(rem != null ? String(rem) : undefined);
      setLastEmailDate(proj?.last_email_date ? String(proj.last_email_date) : undefined);
      if (nextScenario) {
        setForm((s) => ({ ...s, scenario: nextScenario }));
        await maybeFetchEmailTemplate(nextScenario, v);
      } else {
        await maybeFetchEmailTemplate(form.scenario, v);
      }
    } catch (e) {
      // silently ignore for now; fields remain as '-'
      setEndDate(undefined);
      setEstimatedHours(undefined);
      setUtilizedHours(undefined);
      setRemainingHours(undefined);
      setLastEmailDate(undefined);
    } finally {
      setLoadingProject(false);
    }
  };

  const handleScenarioChange = (v: string) => {
    setForm((s) => ({ ...s, scenario: v }));
    if (form.project) {
      void maybeFetchEmailTemplate(v, form.project);
    }
  };

  const canSubmit = useMemo(() => {
    return (
      !!form.client &&
      !!form.project &&
      !!form.scenario &&
      !!form.to &&
      !!form.subject &&
      !!form.content
    );
  }, [form]);

  // Prefill flow: set client first, then when project options are ready select project
  useEffect(() => {
    if (didApplyPrefill.current) return;
    if (!prefill || !prefill.clientId) return;
    if (!form.client) {
      void handleClientChange(prefill.clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    if (didApplyPrefill.current) return;
    if (!prefill || !prefill.clientId || !prefill.projectId) return;
    if (form.client !== prefill.clientId) return;
    const hasProject = projectOptionsLocal.some((p) => p.value === prefill.projectId);
    if (hasProject) {
      didApplyPrefill.current = true;
      void handleProjectChange(prefill.projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectOptionsLocal, form.client, prefill]);

  return (
    <form
      className="p-6 flex flex-col gap-5"
      onSubmit={async (e) => {
        e.preventDefault();
        if (canSubmit) {
          try {
            setSubmitting(true);
            await onSubmit({
              ...form,
              end_date: endDate,
              esti_hrs: estimatedHours ?? "",
              util_hrs: utilizedHours ?? "",
              remi_hrs: remainingHours ?? "",
            } as any);
          } finally {
            setSubmitting(false);
          }
        }
      }}
    >
      <p className="text-sm text-[#6C6C6C]">
        <span className="text-[#FF3B30]">*</span> indicates mandatory fields
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Client name" required />
          <FilterSelectWithRadio
            options={clientOptions}
            width="w-full"
            bgColor="bg-[#FFFFFF]"
            value={form.client}
            onChange={handleClientChange}
            showSearch
            searchPlaceholder="Select client"
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="Project" required />
          <FilterSelectWithRadio
            options={projectOptionsLocal}
            width="w-full"
            bgColor="bg-[#FFFFFF]"
            value={form.project}
            onChange={handleProjectChange}
            showSearch
            searchPlaceholder="Select project"
          />
        </div>
      </div>

      {form.project && lastEmailDate ? (
        <div className="text-sm text-[#1E1E1E]">
          Last email was sent on {lastEmailDate}{" "}
          <button
            type="button"
            className="text-[#6093A0] cursor-pointer underline"
            onClick={() => {
              if (lastEmailDate && onViewEmails) onViewEmails(lastEmailDate);
            }}
          >
            View emails
          </button>
        </div>
      ) : null}

      <div className="flex w-[50%] flex-col gap-1">
        <FieldLabel label="Scenario" required />
        <FilterSelectWithRadio
          options={limitedScenarioOptions}
          width="w-full"
          bgColor="bg-[#FFFFFF]"
          value={form.scenario}
          onChange={handleScenarioChange}
          showSearch
          searchPlaceholder="Select scenario"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <InfoField label="End date" value={loadingProject ? "Loading..." : endDate} danger={isEndDatePast} />
        <InfoField label="Estimated hours" value={loadingProject ? "Loading..." : estimatedHours} />
        <InfoField label="Utilized hours" value={loadingProject ? "Loading..." : utilizedHours} danger={isRemainingNegative} />
        <InfoField label="Remaining hours" value={loadingProject ? "Loading..." : remainingHours} danger={isRemainingNegative} />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="To" required />
        <input
          type="email"
          placeholder="Enter email address"
          className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
          value={form.to}
          onChange={(e) => setForm((s) => ({ ...s, to: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Cc" />
          <input
            type="text"
            placeholder="Enter cc recipients"
            className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
            value={form.cc}
            onChange={(e) => setForm((s) => ({ ...s, cc: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="Bcc" />
          <input
            type="text"
            placeholder="Enter bcc recipients"
            className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
            value={form.bcc}
            onChange={(e) => setForm((s) => ({ ...s, bcc: e.target.value }))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Subject" required />
        <input
          type="text"
          placeholder="Enter subject"
          // disabled
          className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
          value={loadingTemplate ? "Loading template..." : form.subject}
          onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
        />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Email content" required />
        <div className="">
          <QuillEditor
            value={loadingTemplate ? "" : form.content}
            onChange={(val: string) => setForm((s) => ({ ...s, content: val }))}
            modules={quillModules}
            placeholder="Enter email content"
            className="min-h-[160px]"
          // readOnly
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-[36px] px-4 rounded-md bg-[#F5F5F5] text-[#1E1E1E] text-sm hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="h-[36px] px-4 rounded-md text-white text-sm bg-[#73B5C5] hover:bg-[#5da7b8] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending..." : "Send email"}
        </button>
      </div>
    </form>
  );
};

export default NewEmailForm;
